import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { CheckIcon, EyeIcon, RefreshIcon, ServerIcon, TrashIcon } from '@/components/icons';
import { formatDateTime } from '@/utils/date';
import type { NginxConfig } from '@/types/nginx';
import { ActiveBadge, ReloadStatusIndicator } from './NginxStatus';

/**
 * Row actions of the nginx configs table
 */
export type NginxConfigRowAction = 'view' | 'activate' | 'regenerate' | 'delete';

export interface NginxConfigsTableProps {
  configs: NginxConfig[];
  isLoading: boolean;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** Shown in the empty state when set (user may generate configs) */
  onGenerateFirst?: () => void;
  getDomainName: (domainId: string | null) => string;
  getNodeName: (nodeId: string | null) => string;
  /** Which write actions are available for a row (view is always available) */
  getActions: (config: NginxConfig) => { activate: boolean; regenerate: boolean; delete: boolean };
  isBusy: (action: NginxConfigRowAction, configId: string) => boolean;
  onAction: (action: NginxConfigRowAction, config: NginxConfig) => void;
}

const COLUMN_COUNT = 6;

/**
 * NGINX configurations table
 */
export function NginxConfigsTable({
  configs,
  isLoading,
  hasActiveFilters,
  onClearFilters,
  onGenerateFirst,
  getDomainName,
  getNodeName,
  getActions,
  isBusy,
  onAction,
}: NginxConfigsTableProps) {
  return (
    <Table isLoading={isLoading} skeletonColumns={COLUMN_COUNT} skeletonRows={5}>
      <TableHead>
        <TableRow>
          <TableHeader>Domain</TableHeader>
          <TableHeader>Node</TableHeader>
          <TableHeader>Active</TableHeader>
          <TableHeader>Last Reload</TableHeader>
          <TableHeader>Reload Status</TableHeader>
          <TableHeader align="right">Actions</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {configs.length === 0 && !isLoading ? (
          <TableRow>
            <TableCell colSpan={COLUMN_COUNT}>
              <TableEmpty
                icon={<ServerIcon />}
                message={
                  hasActiveFilters ? 'No configurations match your filters' : 'No NGINX configurations yet'
                }
                action={
                  hasActiveFilters ? (
                    <Button variant="secondary" size="sm" onClick={onClearFilters}>
                      Clear filters
                    </Button>
                  ) : (
                    onGenerateFirst && (
                      <Button variant="secondary" size="sm" onClick={onGenerateFirst}>
                        Generate your first config
                      </Button>
                    )
                  )
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          configs.map((config) => {
            const domainName = getDomainName(config.domain_id);
            const actions = getActions(config);

            return (
              <TableRow key={config.id}>
                <TableCell>
                  <span className="nginx-domain">{domainName}</span>
                </TableCell>
                <TableCell>
                  <span className="nginx-node">{getNodeName(config.node_id)}</span>
                </TableCell>
                <TableCell>
                  <ActiveBadge isActive={config.is_active} />
                </TableCell>
                <TableCell>
                  <span className="nginx-reload-time">{formatDateTime(config.last_reload_at)}</span>
                </TableCell>
                <TableCell>
                  <ReloadStatusIndicator status={config.last_reload_status} />
                </TableCell>
                <TableCell align="right">
                  <div className="nginx-actions">
                    <IconButton
                      className="nginx-action-btn"
                      icon={<EyeIcon />}
                      label={`View configuration for ${domainName}`}
                      title="View configuration"
                      onClick={() => onAction('view', config)}
                    />
                    {actions.activate && (
                      <IconButton
                        className="nginx-action-btn nginx-action-btn-activate"
                        icon={<CheckIcon />}
                        label={`Activate configuration for ${domainName}`}
                        title="Activate configuration"
                        isBusy={isBusy('activate', config.id)}
                        onClick={() => onAction('activate', config)}
                      />
                    )}
                    {actions.regenerate && (
                      <IconButton
                        className="nginx-action-btn nginx-action-btn-regenerate"
                        icon={<RefreshIcon />}
                        label={`Regenerate configuration for ${domainName}`}
                        title="Regenerate configuration"
                        isBusy={isBusy('regenerate', config.id)}
                        onClick={() => onAction('regenerate', config)}
                      />
                    )}
                    {actions.delete && (
                      <IconButton
                        className="nginx-action-btn nginx-action-btn-danger"
                        icon={<TrashIcon />}
                        label={`Delete configuration for ${domainName}`}
                        title="Delete configuration"
                        onClick={() => onAction('delete', config)}
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
